import {
  EventNote as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Task as TaskIcon,
  Visibility as ViewIcon,
  Assignment as WorkIcon,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  ButtonBase,
  Container,
  Divider,
  Grid,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  format,
  formatDistanceToNow,
  isAfter,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  subDays,
  subMonths,
  subQuarters,
} from "date-fns";
import { sv } from "date-fns/locale";
import React, { useMemo, useState } from "react";
import { useActivityPanel } from "../../hooks/useActivityPanel";
import { useWorkPanel } from "../../hooks/useWorkPanel";
import { useGetUserWorkItemsQuery } from "../../store/services/workItemService";
import { useGetWorksByUserQuery } from "../../store/services/workService";
import {
  WorkItemList,
  WorkItemStateEnum,
  WorkList,
} from "../../store/types/work.types";
import StatCard from "../ui/cards/StatCard";
import WorkListItem from "../workitem/WorkListItem";
import WorkItemListItem from "../workitem/WorkItemListItem";

interface UserActivityDashboardProps {
  userId: number;
  userName?: string;
}

type TimelineView = "all" | "works" | "workItems";

type StepMode = "day" | "month" | "quarter";

const STEP_MODE_LABELS: Record<StepMode, string> = {
  day: "Dag",
  month: "Månad",
  quarter: "Kvartal",
};

// Svensk month name med stor bokstav.
const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

// Unified timeline entry. Vi delar upp listan i två sektioner:
//
// - Kommande: WorkItems med ScheduledTo > now (sorteras ascending på
//   ScheduledTo — tidigast först, som en kalender).
// - Senaste: Works (sorteras på Modified) + WorkItems utan framtida
//   ScheduledTo (sorteras på Modified, descending).
type TimelineEntry =
  | { kind: "work"; item: WorkList; sortByDate: Date }
  | { kind: "workItem"; item: WorkItemList; sortByDate: Date };

const safeParse = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  try {
    return parseISO(value);
  } catch {
    return null;
  }
};

const getModifiedDate = (modified: string | undefined): Date =>
  safeParse(modified) ?? new Date(0);

interface DashboardStats {
  totalActivities: number;
  recentActivities: number; // senaste 7 dagarna
  worksCount: number;
  workItemsCount: number;
  activitiesByType: Record<"works" | "workItems", number>;
  activitiesByMonth: Record<string, number>;
}

const calculateStats = (
  works: WorkList[],
  workItems: WorkItemList[]
): DashboardStats => {
  const lastWeek = subDays(new Date(), 7);

  const stats: DashboardStats = {
    totalActivities: works.length + workItems.length,
    recentActivities: 0,
    worksCount: works.length,
    workItemsCount: workItems.length,
    activitiesByType: {
      works: works.length,
      workItems: workItems.length,
    },
    activitiesByMonth: {},
  };

  // Grupperar på "effektivt datum":
  // - Work: Modified (det har inget ScheduledTo)
  // - WorkItem: ScheduledTo (när den ska göras), fallback Modified för
  //   aktiviteter utan schemaläggning.
  // Detta matchar stakeholder-frågan "vilken månad tillhör aktiviteten?"
  // snarare än "när rördes posten senast".
  const bumpMonth = (date: Date | null) => {
    if (!date) return;
    if (isAfter(date, lastWeek)) stats.recentActivities += 1;
    const key = format(date, "yyyy-MM");
    stats.activitiesByMonth[key] = (stats.activitiesByMonth[key] ?? 0) + 1;
  };

  works.forEach((w) => bumpMonth(safeParse(w.Modified)));
  workItems.forEach((wi) =>
    bumpMonth(safeParse(wi.ScheduledTo) ?? safeParse(wi.Modified))
  );

  return stats;
};

const getTimeAgo = (date: Date): string =>
  formatDistanceToNow(date, { addSuffix: true, locale: sv });

// Små helpers för rendering av stats per typ (lokal färg-mapping)
const typeColor = (kind: "works" | "workItems", theme: Theme): string =>
  kind === "works"
    ? theme.palette.primary.main
    : theme.palette.secondary.main;

const typeLabel = (kind: "works" | "workItems"): string =>
  kind === "works" ? "Ärenden" : "Aktiviteter";

const typeIcon = (kind: "works" | "workItems"): React.ReactNode =>
  kind === "works" ? <WorkIcon /> : <TaskIcon />;

// ---------- StatsSection ----------

const StatsSection: React.FC<{
  stats: DashboardStats;
  userId: number;
  isLoading: boolean;
}> = ({ stats, userId, isLoading }) => {
  const theme = useTheme();
  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Totala aktiviteter"
          description={stats.totalActivities.toString()}
          subtitle="Ärenden + aktiviteter"
          icon={<ViewIcon fontSize="large" />}
          color={theme.palette.primary.main}
          to={`/users/${userId}/activity`}
          loading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Senaste veckan"
          description={stats.recentActivities.toString()}
          subtitle="Uppdaterade senaste 7 dagar"
          icon={<CalendarIcon fontSize="large" />}
          color={theme.palette.info.main}
          to={`/users/${userId}/activity`}
          loading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Ärenden"
          description={stats.worksCount.toString()}
          subtitle="Skapade eller uppdaterade"
          icon={<WorkIcon fontSize="large" />}
          color={theme.palette.primary.main}
          to={`/users/${userId}/activity`}
          loading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Aktiviteter"
          description={stats.workItemsCount.toString()}
          subtitle="Skapade eller uppdaterade"
          icon={<TaskIcon fontSize="large" />}
          color={theme.palette.secondary.main}
          to={`/users/${userId}/activity`}
          loading={isLoading}
        />
      </Grid>
    </Grid>
  );
};

// ---------- TimelineSection ----------

interface TimelineSectionProps {
  upcomingEntries: TimelineEntry[];
  recentEntries: TimelineEntry[];
  timelineView: TimelineView;
  onTimelineViewChange: (view: TimelineView) => void;
  isLoading: boolean;
  onOpenWork: (workId: number) => void;
  onOpenActivity: (activityId: number) => void;
}

const TimelineSection: React.FC<TimelineSectionProps> = ({
  upcomingEntries,
  recentEntries,
  timelineView,
  onTimelineViewChange,
  isLoading,
  onOpenWork,
  onOpenActivity,
}) => {
  const theme = useTheme();

  const filterByView = (entries: TimelineEntry[]) => {
    if (timelineView === "all") return entries;
    const kind = timelineView === "works" ? "work" : "workItem";
    return entries.filter((e) => e.kind === kind);
  };

  const filteredUpcoming = useMemo(
    () => filterByView(upcomingEntries),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [upcomingEntries, timelineView]
  );
  const filteredRecent = useMemo(
    () => filterByView(recentEntries),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentEntries, timelineView]
  );

  const totalCount = filteredUpcoming.length + filteredRecent.length;

  return (
    <Paper sx={{ p: 0, mb: 4, borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, pt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Aktivitetstidslinje
        </Typography>
        <Tabs
          value={timelineView}
          onChange={(_, value: TimelineView) => onTimelineViewChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1 }}
        >
          <Tab label="Alla" value="all" />
          <Tab label="Ärenden" value="works" />
          <Tab label="Aktiviteter" value="workItems" />
        </Tabs>
      </Box>

      <Divider />

      <Box sx={{ px: 3, py: 3, maxHeight: 720, overflowY: "auto" }}>
        {isLoading ? (
          <Stack spacing={2}>
            {[...Array(4)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={96}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        ) : totalCount === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              Inga aktiviteter att visa
            </Typography>
          </Box>
        ) : (
          <Stack spacing={4}>
            {filteredUpcoming.length > 0 && (
              <Box>
                <SectionHeader
                  label={`Kommande · ${filteredUpcoming.length}`}
                />
                <Stack spacing={2}>
                  {filteredUpcoming.map((entry) =>
                    renderEntry(entry, "upcoming", theme, onOpenWork, onOpenActivity)
                  )}
                </Stack>
              </Box>
            )}
            {filteredRecent.length > 0 && (
              <Box>
                <SectionHeader label={`Senaste · ${filteredRecent.length}`} />
                <Stack spacing={2}>
                  {filteredRecent.map((entry) =>
                    renderEntry(entry, "recent", theme, onOpenWork, onOpenActivity)
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

// Sektionsrubrik — används för "Kommande" / "Senaste" i timelinen.
const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{
      display: "block",
      fontWeight: 700,
      letterSpacing: "0.08em",
      mb: 1.5,
    }}
  >
    {label}
  </Typography>
);

// Delad rendering av en enskild timeline-entry. `section` styr hur captionen
// formuleras ("Schemalagd om X" i Kommande, "Uppdaterad för X sedan" i Senaste).
const renderEntry = (
  entry: TimelineEntry,
  section: "upcoming" | "recent",
  theme: Theme,
  onOpenWork: (id: number) => void,
  onOpenActivity: (id: number) => void
) => {
  const itemKey = `${entry.kind}-${entry.item.Id}`;
  const relativeTime = getTimeAgo(entry.sortByDate);
  const kindLabel = entry.kind === "work" ? "Ärende" : "Aktivitet";
  const timeLabel =
    section === "upcoming"
      ? `Schemalagd ${relativeTime}`
      : `Uppdaterad ${relativeTime}`;

  return (
    <Box key={itemKey}>
      {/* Relativ tid som en liten caption ovanför varje kort */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.75, pl: 0.5 }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor:
              entry.kind === "work"
                ? theme.palette.primary.main
                : theme.palette.secondary.main,
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          {kindLabel} · {timeLabel}
        </Typography>
      </Stack>

      {entry.kind === "work" ? (
        // WorkListItem saknar egen ram — wrappa i Paper så den matchar
        // övriga kort visuellt. Hela Paperet är klickbart och öppnar
        // work-panelen.
        <Paper
          elevation={0}
          onClick={() => onOpenWork(entry.item.Id)}
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            cursor: "pointer",
            transition: "all 0.15s",
            "&:hover": {
              borderColor: alpha(theme.palette.primary.main, 0.4),
              boxShadow: `0 2px 12px ${alpha(
                theme.palette.common.black,
                0.06
              )}`,
            },
          }}
        >
          <WorkListItem
            workType={entry.item.Type}
            title={entry.item.Name}
            description={entry.item.Description}
            customerName={entry.item.Customer?.Name}
            state={entry.item.State}
            categoryName={entry.item.Category?.Name}
            teamName={entry.item.Team?.Name}
            teamId={entry.item.Team?.Id}
            assignedToName={entry.item.AssignedTo?.Name}
            modified={entry.item.Modified}
          />
        </Paper>
      ) : (
        // WorkItemListItem har sin egen ram — ingen Paper-wrapper.
        <Box
          onClick={() => onOpenActivity(entry.item.Id)}
          sx={{
            cursor: "pointer",
            borderRadius: 1,
            transition: "all 0.15s",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: `0 4px 16px ${alpha(
                theme.palette.common.black,
                0.08
              )}`,
            },
          }}
        >
          <WorkItemListItem
            workItemType={entry.item.Type}
            title={entry.item.Name}
            description={entry.item.Description}
            date={entry.item.ScheduledTo}
            customerName={entry.item.Customer?.Name}
            isCompleted={entry.item.State === WorkItemStateEnum.Closed}
            isActive={entry.item.State === WorkItemStateEnum.Active}
            isOverdue={
              entry.item.State === WorkItemStateEnum.Active &&
              !!entry.item.ScheduledTo &&
              isAfter(new Date(), parseISO(entry.item.ScheduledTo))
            }
            resultData={entry.item.Result}
            workId={entry.item.WorkId}
            showAction={false}
          />
        </Box>
      )}
    </Box>
  );
};

// ---------- ActivityByTypeSection ----------

const ActivityByTypeSection: React.FC<{ stats: DashboardStats }> = ({
  stats,
}) => {
  const theme = useTheme();

  const maxMonth = Math.max(...Object.values(stats.activitiesByMonth), 1);
  const byTypeEntries = Object.entries(stats.activitiesByType) as [
    "works" | "workItems",
    number
  ][];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>
            Aktiviteter per typ
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {stats.totalActivities === 0 ? (
            <Typography color="text.secondary" align="center">
              Ingen data att visa
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {byTypeEntries.map(([kind, count]) => {
                const color = typeColor(kind, theme);
                return (
                  <Box
                    key={kind}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: alpha(color, 0.15),
                        color,
                        mr: 2,
                      }}
                    >
                      {typeIcon(kind)}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1">
                        {typeLabel(kind)}
                      </Typography>
                      <Box
                        sx={{
                          height: 8,
                          bgcolor: alpha(color, 0.15),
                          borderRadius: 4,
                          mt: 0.5,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            height: "100%",
                            width: `${
                              (count / stats.totalActivities) * 100
                            }%`,
                            bgcolor: color,
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        ml: 2,
                        minWidth: 50,
                        textAlign: "right",
                        fontWeight: "bold",
                      }}
                    >
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>
            Aktiviteter över tid
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {Object.entries(stats.activitiesByMonth).length === 0 ? (
            <Typography color="text.secondary" align="center">
              Ingen data att visa
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {Object.entries(stats.activitiesByMonth)
                .sort()
                .map(([month, count]) => {
                  const [year, monthNum] = month.split("-");
                  const monthName = new Date(
                    parseInt(year),
                    parseInt(monthNum) - 1,
                    1
                  ).toLocaleDateString("sv-SE", { month: "long" });

                  return (
                    <Box
                      key={month}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          minWidth: 42,
                          height: 42,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          color: theme.palette.primary.main,
                          mr: 2,
                        }}
                      >
                        <CalendarIcon />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1">
                          {monthName} {year}
                        </Typography>
                        <Box
                          sx={{
                            height: 8,
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            borderRadius: 4,
                            mt: 0.5,
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              height: "100%",
                              width: `${(count / maxMonth) * 100}%`,
                              bgcolor: theme.palette.primary.main,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 2,
                          minWidth: 50,
                          textAlign: "right",
                          fontWeight: "bold",
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

// ---------- Root ----------

const UserActivityDashboard: React.FC<UserActivityDashboardProps> = ({
  userId,
  userName = "Användaren",
}) => {
  const theme = useTheme();
  const [timelineView, setTimelineView] = useState<TimelineView>("all");
  // Generisk stepper — offset = N steg bakåt från nu i den valda enheten.
  // 0 = nuvarande period, 1 = föregående, osv.
  const [stepMode, setStepMode] = useState<StepMode>("quarter");
  const [offset, setOffset] = useState(0);
  const [modeMenuAnchor, setModeMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const { openWork } = useWorkPanel();
  const { openActivity } = useActivityPanel();

  const handleModeSelect = (mode: StepMode) => {
    setStepMode(mode);
    // Nollställ offset vid byte av enhet — "2 kvartal bakåt" blir inte
    // automatiskt "2 dagar bakåt" om man byter till dag-läge.
    setOffset(0);
    setModeMenuAnchor(null);
  };

  // Perioden beräknas utifrån vald enhet + offset. Resultatet är alltid
  // hela kalenderperioder (hela kvartal/månad/dag) i lokal tid.
  const { fromDate, toDate, rangeStart, rangeEnd, periodLabel } =
    useMemo(() => {
      const today = new Date();
      let start: Date;
      let end: Date;
      let label: string;

      switch (stepMode) {
        case "day": {
          const anchor = subDays(today, offset);
          start = startOfDay(anchor);
          end = endOfDay(anchor);
          label = capitalize(
            format(start, "EEEE d MMM yyyy", { locale: sv })
          );
          break;
        }
        case "month": {
          const anchor = subMonths(today, offset);
          start = startOfMonth(anchor);
          end = endOfMonth(anchor);
          label = capitalize(format(start, "MMMM yyyy", { locale: sv }));
          break;
        }
        case "quarter":
        default: {
          const anchor = subQuarters(today, offset);
          start = startOfQuarter(anchor);
          end = endOfQuarter(anchor);
          const q = Math.floor(start.getMonth() / 3) + 1;
          label = `Kv${q} ${start.getFullYear()}`;
          break;
        }
      }

      return {
        fromDate: format(start, "yyyy-MM-dd"),
        toDate: format(end, "yyyy-MM-dd"),
        rangeStart: start,
        rangeEnd: end,
        periodLabel: label,
      };
    }, [stepMode, offset]);

  const rangeCaption = useMemo(() => {
    // I dag-läge skulle caption bara upprepa period-labeln → hoppa den.
    if (stepMode === "day") return null;
    const fromStr = format(rangeStart, "d MMM", { locale: sv });
    const toStr = format(rangeEnd, "d MMM yyyy", { locale: sv });
    return `${fromStr} – ${toStr}`;
  }, [rangeStart, rangeEnd, stepMode]);

  const stepTooltip = (direction: "back" | "forward"): string => {
    const unit =
      stepMode === "day"
        ? "dag"
        : stepMode === "month"
        ? "månad"
        : "kvartal";
    return direction === "back" ? `Föregående ${unit}` : `Nästa ${unit}`;
  };

  const queryParams = useMemo(
    () => ({ userId, fromDate, toDate }),
    [userId, fromDate, toDate]
  );

  const { data: works = [], isLoading: isLoadingWorks } =
    useGetWorksByUserQuery(queryParams);
  const { data: workItems = [], isLoading: isLoadingWorkItems } =
    useGetUserWorkItemsQuery(queryParams);

  const isLoading = isLoadingWorks || isLoadingWorkItems;

  const { upcomingEntries, recentEntries } = useMemo(() => {
    const now = new Date();
    const upcoming: TimelineEntry[] = [];
    const recent: TimelineEntry[] = [];

    works.forEach((w) => {
      // Works har inget ScheduledTo — ligger alltid i "Senaste".
      recent.push({
        kind: "work",
        item: w,
        sortByDate: getModifiedDate(w.Modified),
      });
    });

    workItems.forEach((wi) => {
      const scheduled = safeParse(wi.ScheduledTo);
      if (scheduled && isAfter(scheduled, now)) {
        upcoming.push({ kind: "workItem", item: wi, sortByDate: scheduled });
      } else {
        recent.push({
          kind: "workItem",
          item: wi,
          sortByDate: getModifiedDate(wi.Modified),
        });
      }
    });

    // Kommande: tidigast först (som en kalender).
    upcoming.sort((a, b) => a.sortByDate.getTime() - b.sortByDate.getTime());
    // Senaste: senast modifierat först.
    recent.sort((a, b) => b.sortByDate.getTime() - a.sortByDate.getTime());

    return { upcomingEntries: upcoming, recentEntries: recent };
  }, [works, workItems]);

  const stats = useMemo(
    () => calculateStats(works, workItems),
    [works, workItems]
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Aktivitetsdashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Aktivitetsöversikt för {userName}
          </Typography>
        </Box>

        {/* Period-stepper — klick på label öppnar mode-menyn */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Tooltip title={stepTooltip("back")}>
            <IconButton
              size="small"
              onClick={() => setOffset((n) => n + 1)}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Byt enhet">
            <ButtonBase
              onClick={(e) => setModeMenuAnchor(e.currentTarget)}
              sx={{
                minWidth: 170,
                textAlign: "center",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                transition: "background-color 0.15s",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                {periodLabel}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.2 }}
              >
                {rangeCaption ?? STEP_MODE_LABELS[stepMode]}
              </Typography>
            </ButtonBase>
          </Tooltip>

          <Menu
            anchorEl={modeMenuAnchor}
            open={Boolean(modeMenuAnchor)}
            onClose={() => setModeMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
          >
            {(Object.keys(STEP_MODE_LABELS) as StepMode[]).map((mode) => (
              <MenuItem
                key={mode}
                selected={stepMode === mode}
                onClick={() => handleModeSelect(mode)}
              >
                <ListItemText primary={STEP_MODE_LABELS[mode]} />
              </MenuItem>
            ))}
          </Menu>

          <Tooltip
            title={offset === 0 ? "Redan senaste" : stepTooltip("forward")}
          >
            <span>
              <IconButton
                size="small"
                onClick={() => setOffset((n) => Math.max(0, n - 1))}
                disabled={offset === 0}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Paper>
      </Stack>

      <StatsSection stats={stats} userId={userId} isLoading={isLoading} />
      <TimelineSection
        upcomingEntries={upcomingEntries}
        recentEntries={recentEntries}
        timelineView={timelineView}
        onTimelineViewChange={setTimelineView}
        isLoading={isLoading}
        onOpenWork={openWork}
        onOpenActivity={openActivity}
      />
      <ActivityByTypeSection stats={stats} />
    </Container>
  );
};

export default UserActivityDashboard;
